<template>
  <div class="capacity-cockpit">
    <ContentWrap class="mb-16px">
      <div class="header-row">
        <div>
          <h2 class="title">总裁驾驶舱</h2>
          <p class="subtitle">配送经营结果 · 今日指标与近 7 日趋势（只读）</p>
        </div>
        <el-button type="primary" plain :loading="loading" @click="loadAll">
          <Icon icon="ep:refresh" class="mr-4px" /> 刷新
        </el-button>
      </div>
    </ContentWrap>

    <el-row :gutter="16" class="mb-16px">
      <el-col :xs="24" :sm="12" :lg="6" class="mb-16px">
        <el-card shadow="hover" class="kpi-card kpi-card--primary" @click="goTo('/distribution/platform/waybill')">
          <div class="kpi-label">今日运费</div>
          <div class="kpi-value">
            <el-skeleton v-if="loading" :rows="1" animated />
            <span v-else>¥{{ formatMoney(summary.todayFeeTotal) }}</span>
          </div>
          <div class="kpi-extra">口径：今日完单运费合计（非商品货款）</div>
        </el-card>
      </el-col>
      <el-col :xs="24" :sm="12" :lg="6" class="mb-16px">
        <el-card shadow="hover" class="kpi-card" @click="goTo('/distribution/platform/waybill')">
          <div class="kpi-label">今日完单</div>
          <div class="kpi-value">
            <el-skeleton v-if="loading" :rows="1" animated />
            <span v-else>{{ summary.todayCompletedCount ?? 0 }}</span>
          </div>
        </el-card>
      </el-col>
      <el-col :xs="24" :sm="12" :lg="6" class="mb-16px">
        <el-card shadow="hover" class="kpi-card" @click="goTo('/distribution/platform/waybill')">
          <div class="kpi-label">进行中</div>
          <div class="kpi-value">
            <el-skeleton v-if="loading" :rows="1" animated />
            <span v-else>{{ summary.processingCount ?? 0 }}</span>
          </div>
        </el-card>
      </el-col>
      <el-col :xs="24" :sm="12" :lg="6" class="mb-16px">
        <el-card shadow="hover" class="kpi-card" @click="goTo('/distribution/platform/waybill?tab=todos')">
          <div class="kpi-label">卡住待办</div>
          <div class="kpi-value">
            <el-skeleton v-if="loading" :rows="1" animated />
            <span v-else>{{ summary.stuckTodoCount ?? 0 }}</span>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="16" class="mb-16px">
      <el-col v-for="card in secondaryCards" :key="card.key" :xs="24" :sm="12" :lg="6" class="mb-16px">
        <el-card shadow="hover" class="stat-card" @click="goTo(card.path)">
          <div class="stat-content">
            <div class="stat-icon" :style="{ background: card.bg }">
              <Icon :icon="card.icon" :size="26" :style="{ color: card.color }" />
            </div>
            <div class="stat-info">
              <div class="stat-value">
                <el-skeleton v-if="loading" :rows="1" animated />
                <span v-else>{{ card.value }}</span>
              </div>
              <div class="stat-label">{{ card.title }}</div>
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <ContentWrap title="近 7 日完单与运费">
      <Echart :height="360" :options="trendChartOptions" />
    </ContentWrap>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue'
import { useRouter } from 'vue-router'
import type { EChartsOption } from 'echarts'
import Echart from '@/components/Echart/src/Echart.vue'
import { ContentWrap } from '@/components/ContentWrap'
import {
  getCapacityDashboardSummary,
  getCapacityDashboardTrend,
  type CapacityDashboardSummary
} from '@/api/capacity/dashboard'
import { pushDistributionPath } from '@/utils/distributionNavigate'

defineOptions({ name: 'CapacityCockpit' })

const router = useRouter()
const loading = ref(false)
const summary = ref<CapacityDashboardSummary>({})

const secondaryCards = computed(() => {
  const s = summary.value
  return [
    {
      key: 'online',
      title: '在线骑手',
      value: s.onlineRiderCount ?? 0,
      icon: 'ep:bicycle',
      color: '#409eff',
      bg: 'rgba(64,158,255,0.1)',
      path: '/distribution/platform/rider-mgmt'
    },
    {
      key: 'approved',
      title: '已通过认证',
      value: s.approvedCertCount ?? 0,
      icon: 'ep:circle-check',
      color: '#67c23a',
      bg: 'rgba(103,194,58,0.1)',
      path: '/distribution/platform/rider-mgmt?tab=cert'
    },
    {
      key: 'org',
      title: '开通机构',
      value: s.activeOrgCount ?? 0,
      icon: 'ep:office-building',
      color: '#0d9488',
      bg: 'rgba(13,148,136,0.1)',
      path: '/distribution/platform/org'
    },
    {
      key: 'cancel',
      title: '今日取消',
      value: s.todayCancelledCount ?? 0,
      icon: 'ep:close',
      color: '#f56c6c',
      bg: 'rgba(245,108,108,0.1)',
      path: '/distribution/platform/waybill'
    }
  ]
})

const trendChartOptions = reactive<EChartsOption>({
  tooltip: { trigger: 'axis' },
  legend: { data: ['运费（元）', '完单数'], top: 0 },
  grid: { left: 50, right: 50, top: 40, bottom: 30 },
  xAxis: { type: 'category', data: [] as string[], boundaryGap: false },
  yAxis: [
    { type: 'value', name: '运费', splitLine: { lineStyle: { type: 'dashed' } } },
    { type: 'value', name: '完单', splitLine: { show: false } }
  ],
  series: [
    {
      name: '运费（元）',
      type: 'line',
      smooth: true,
      data: [] as number[],
      itemStyle: { color: '#e85d04' },
      areaStyle: { opacity: 0.08 }
    },
    {
      name: '完单数',
      type: 'line',
      smooth: true,
      yAxisIndex: 1,
      data: [] as number[],
      itemStyle: { color: '#409eff' },
      areaStyle: { opacity: 0.06 }
    }
  ]
})

function formatMoney(v?: number) {
  return Number(v ?? 0).toLocaleString('zh-CN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })
}

function goTo(path?: string) {
  if (path) void pushDistributionPath(router, path)
}

async function loadAll() {
  loading.value = true
  try {
    const [s, t] = await Promise.all([
      getCapacityDashboardSummary(),
      getCapacityDashboardTrend()
    ])
    summary.value = s || {}
    const dates = t?.dates || []
    const fees = (t?.feeTotal || []).map((n) => Number(n || 0))
    const counts = (t?.completedCount || []).map((n) => Number(n || 0))
    ;(trendChartOptions.xAxis as any).data = dates
    ;(trendChartOptions.series as any)[0].data = fees
    ;(trendChartOptions.series as any)[1].data = counts
  } finally {
    loading.value = false
  }
}

onMounted(loadAll)
</script>

<style scoped lang="scss">
.header-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}
.title {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
}
.subtitle {
  margin: 6px 0 0;
  font-size: 13px;
  color: var(--el-text-color-secondary);
}
.kpi-card {
  cursor: pointer;
  min-height: 120px;
}
.kpi-card--primary {
  background: linear-gradient(135deg, rgba(232, 93, 4, 0.08), #fff);
}
.kpi-label {
  font-size: 13px;
  color: var(--el-text-color-secondary);
}
.kpi-value {
  margin-top: 10px;
  font-size: 28px;
  font-weight: 800;
}
.kpi-extra {
  margin-top: 8px;
  font-size: 12px;
  color: var(--el-text-color-secondary);
}
.stat-card {
  cursor: pointer;
}
.stat-content {
  display: flex;
  align-items: center;
  gap: 14px;
}
.stat-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.stat-value {
  font-size: 22px;
  font-weight: 700;
}
.stat-label {
  margin-top: 4px;
  font-size: 13px;
  color: var(--el-text-color-secondary);
}
</style>
